import { useQuery } from '@tanstack/react-query';

import type { FileQueryKey, UseFileOptions } from './use-file';

import { useFile } from './use-file';

export type UseImageOptions<TData extends Blob | null, TError = unknown, TQueryKey extends FileQueryKey = FileQueryKey>
    = UseFileOptions<TData, TError, TQueryKey>;

export function useImage<TData extends Blob | null, TError = unknown, TQueryKey extends FileQueryKey = FileQueryKey>({
    queryOptions,
    enabled = true,
    metaData,
}: UseImageOptions<TData, TError, TQueryKey>) {
    const { data: blob, ...fileState } = useFile({ queryOptions, enabled, metaData });
    const { data: url, ...urlState } = useQuery({
        queryKey: [...(queryOptions?.queryKey ?? []), 'url', !!blob, blob?.size],
        queryFn: () => {
            if (!(blob instanceof Blob) || blob.size === 0) {
                return null;
            }

            try {
                return URL.createObjectURL(blob);
            }
            catch (error) {
                console.error(error);

                return null;
            }
        },
        // Enabled if the query key is defined, the file is not loading, and the file is a blob and has a size greater than 0
        enabled: enabled && queryOptions?.queryKey && !fileState.isLoading && blob instanceof Blob && blob.size > 0,
        gcTime: 0,
        staleTime: 0,
    });

    return {
        fileState,
        urlState,
        url,
    };
}
